import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, X, Copy, Clock, CalendarX2, Trash2, Loader2, AlertTriangle, CalendarPlus } from "lucide-react";

const EXC_TYPES = [
  { v: "closure", l: "Chiusura straordinaria" },
  { v: "opening", l: "Apertura straordinaria" },
  { v: "reduced", l: "Orario ridotto" },
  { v: "holiday", l: "Ferie del salone" },
  { v: "event", l: "Evento / Formazione" },
];
const EXC_TONE = { closure: "--destructive", holiday: "--destructive", event: "--hi-status-info", opening: "--hi-status-success", reduced: "--hi-gold" };

export function SalonHours() {
  const [weekly, setWeekly] = useState(null);
  const [labels, setLabels] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(null); // {conflicts, count, onConfirm}

  const load = useCallback(() => {
    api.get("/salon/hours").then((r) => {
      setWeekly(r.data.weekly);
      setLabels(r.data.weekday_labels || ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"]);
      setExceptions(r.data.exceptions || []);
    });
  }, []);
  useEffect(() => { load(); }, [load]);

  const setDay = (i, patch) => setWeekly((w) => ({ ...w, [i]: { ...w[i], ...patch } }));
  const setRange = (i, ri, idx, val) => setWeekly((w) => ({ ...w, [i]: { ...w[i], ranges: w[i].ranges.map((r, k) => k === ri ? r.map((x, j) => j === idx ? val : x) : r) } }));
  const addRange = (i) => setWeekly((w) => ({ ...w, [i]: { ...w[i], open: true, ranges: [...(w[i].ranges || []), ["09:00", "13:00"]] } }));
  const rmRange = (i, ri) => setWeekly((w) => ({ ...w, [i]: { ...w[i], ranges: w[i].ranges.filter((_, k) => k !== ri) } }));
  const applyWeekdays = () => {
    const mon = weekly["0"];
    setWeekly((w) => { const n = { ...w }; for (let i = 1; i <= 4; i++) n[i] = { open: mon.open, ranges: mon.ranges.map((r) => [...r]) }; return n; });
    toast.info("Lunedì applicato a Mar–Ven (ricorda di salvare)");
  };

  const doSave = async (force = false) => {
    setSaving(true);
    try {
      const { data } = await api.put("/salon/hours", { weekly, force });
      if (data.needs_confirmation) {
        setConflict({ conflicts: data.conflicts, count: data.count, onConfirm: () => doSave(true) });
        return;
      }
      toast.success("Orari del salone salvati"); setConflict(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore salvataggio"); }
    finally { setSaving(false); }
  };

  // --- Exceptions ---
  const [excOpen, setExcOpen] = useState(false);
  const [exc, setExc] = useState({ type: "closure", start_date: "", end_date: "", title: "", note: "", ranges: [["10:00", "14:00"]] });
  const needsRanges = exc.type === "opening" || exc.type === "reduced";
  const submitExc = async (force = false) => {
    if (!exc.start_date) { toast.error("Seleziona la data"); return; }
    setSaving(true);
    try {
      const payload = { ...exc, end_date: exc.end_date || exc.start_date, ranges: needsRanges ? exc.ranges : undefined, force };
      const { data } = await api.post("/salon/exceptions", payload);
      if (data.needs_confirmation) {
        setConflict({ conflicts: data.conflicts, count: data.count, onConfirm: () => submitExc(true) });
        return;
      }
      toast.success("Eccezione aggiunta"); setExcOpen(false); setConflict(null);
      setExc({ type: "closure", start_date: "", end_date: "", title: "", note: "", ranges: [["10:00", "14:00"]] });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };
  const deleteExc = async (id) => { await api.delete(`/salon/exceptions/${id}`); toast.success("Eccezione rimossa"); load(); };

  if (!weekly) return <div className="h-40 flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-[hsl(var(--hi-gold))]" /></div>;

  return (
    <div className="space-y-6">
      {/* Orari settimanali */}
      <Card className="p-5" data-testid="salon-weekly-card">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-2"><Clock className="h-5 w-5 text-[hsl(var(--hi-gold))]" /><h3 className="font-heading text-xl font-semibold">Orari settimanali</h3></div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={applyWeekdays} data-testid="salon-apply-weekdays"><Copy className="h-4 w-4 mr-1" /> Applica Lun a feriali</Button>
            <Button size="sm" onClick={() => doSave(false)} disabled={saving} data-testid="salon-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva orari"}</Button>
          </div>
        </div>
        <div className="space-y-2">
          {[0, 1, 2, 3, 4, 5, 6].map((i) => {
            const d = weekly[String(i)] || { open: false, ranges: [] };
            return (
              <div key={i} className="flex items-start gap-3 rounded-lg border border-border p-3" data-testid={`salon-day-${i}`}>
                <div className="w-28 shrink-0 pt-1">
                  <div className="font-medium">{labels[i]}</div>
                  <div className="flex items-center gap-2 mt-1">
                    <Switch checked={!!d.open} onCheckedChange={(v) => setDay(String(i), { open: v })} data-testid={`salon-day-toggle-${i}`} />
                    <span className="text-xs text-muted-foreground">{d.open ? "Aperto" : "Chiuso"}</span>
                  </div>
                </div>
                <div className="flex-1 space-y-2">
                  {!d.open && <div className="text-sm text-muted-foreground pt-1.5">Giorno di chiusura</div>}
                  {d.open && (d.ranges || []).map((r, ri) => (
                    <div key={ri} className="flex items-center gap-2" data-testid={`salon-range-${i}-${ri}`}>
                      <Input type="time" value={r[0]} onChange={(e) => setRange(String(i), ri, 0, e.target.value)} className="h-8 w-28" data-testid={`salon-range-start-${i}-${ri}`} />
                      <span className="text-muted-foreground">–</span>
                      <Input type="time" value={r[1]} onChange={(e) => setRange(String(i), ri, 1, e.target.value)} className="h-8 w-28" data-testid={`salon-range-end-${i}-${ri}`} />
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => rmRange(String(i), ri)}><X className="h-3.5 w-3.5" /></Button>
                    </div>
                  ))}
                  {d.open && <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => addRange(String(i))} data-testid={`salon-add-range-${i}`}><Plus className="h-3 w-3 mr-1" /> Aggiungi fascia</Button>}
                </div>
              </div>
            );
          })}
        </div>
        <p className="text-[11px] text-muted-foreground mt-3">Chiudendo un giorno gli orari vengono conservati e ripristinati quando lo riapri.</p>
      </Card>

      {/* Eccezioni */}
      <Card className="p-5" data-testid="salon-exceptions-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2"><CalendarX2 className="h-5 w-5 text-[hsl(var(--hi-gold))]" /><h3 className="font-heading text-xl font-semibold">Eccezioni e chiusure</h3></div>
          <Button size="sm" onClick={() => setExcOpen(true)} data-testid="salon-exc-new"><CalendarPlus className="h-4 w-4 mr-1" /> Nuova eccezione</Button>
        </div>
        {exceptions.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center">Nessuna eccezione. Aggiungi chiusure straordinarie, ferie o aperture speciali.</div>
        ) : (
          <div className="space-y-2">
            {exceptions.map((e) => (
              <div key={e.id} className="flex items-center justify-between rounded-lg border border-border p-3" data-testid={`salon-exc-${e.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: `hsl(var(${EXC_TONE[e.type] || "--muted-foreground"}))` }} />
                    <span className="font-medium truncate">{e.title || EXC_TYPES.find((t) => t.v === e.type)?.l}</span>
                    <Badge variant="secondary" className="text-[10px]">{EXC_TYPES.find((t) => t.v === e.type)?.l}</Badge>
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {fmtDate(e.start_date)}{e.end_date && e.end_date !== e.start_date ? ` → ${fmtDate(e.end_date)}` : ""}
                    {e.ranges?.length ? ` · ${e.ranges.map((r) => `${r[0]}-${r[1]}`).join(", ")}` : ""}
                    {e.note ? ` · ${e.note}` : ""}
                  </div>
                </div>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => deleteExc(e.id)} data-testid={`salon-exc-delete-${e.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Dialog nuova eccezione */}
      <Dialog open={excOpen} onOpenChange={setExcOpen}>
        <DialogContent className="max-w-md" data-testid="salon-exc-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Nuova eccezione</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Tipo</Label>
              <Select value={exc.type} onValueChange={(v) => setExc({ ...exc, type: v })}>
                <SelectTrigger data-testid="exc-type"><SelectValue /></SelectTrigger>
                <SelectContent>{EXC_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5"><Label>Dal</Label><Input type="date" value={exc.start_date} onChange={(e) => setExc({ ...exc, start_date: e.target.value })} data-testid="exc-start" /></div>
              <div className="space-y-1.5"><Label>Al (opzionale)</Label><Input type="date" value={exc.end_date} onChange={(e) => setExc({ ...exc, end_date: e.target.value })} data-testid="exc-end" /></div>
            </div>
            {needsRanges && (
              <div className="space-y-1.5">
                <Label>Fasce orarie</Label>
                {exc.ranges.map((r, ri) => (
                  <div key={ri} className="flex items-center gap-2">
                    <Input type="time" value={r[0]} onChange={(e) => setExc({ ...exc, ranges: exc.ranges.map((x, k) => k === ri ? [e.target.value, x[1]] : x) })} className="h-8 w-28" data-testid={`exc-range-start-${ri}`} />
                    <span>–</span>
                    <Input type="time" value={r[1]} onChange={(e) => setExc({ ...exc, ranges: exc.ranges.map((x, k) => k === ri ? [x[0], e.target.value] : x) })} className="h-8 w-28" data-testid={`exc-range-end-${ri}`} />
                    {exc.ranges.length > 1 && <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setExc({ ...exc, ranges: exc.ranges.filter((_, k) => k !== ri) })}><X className="h-3.5 w-3.5" /></Button>}
                  </div>
                ))}
                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setExc({ ...exc, ranges: [...exc.ranges, ["15:00", "19:00"]] })}><Plus className="h-3 w-3 mr-1" /> Fascia</Button>
              </div>
            )}
            <div className="space-y-1.5"><Label>Titolo</Label><Input value={exc.title} onChange={(e) => setExc({ ...exc, title: e.target.value })} placeholder="Es. Ferie estive" data-testid="exc-title" /></div>
            <div className="space-y-1.5"><Label>Nota interna</Label><Textarea rows={2} value={exc.note} onChange={(e) => setExc({ ...exc, note: e.target.value })} data-testid="exc-note" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExcOpen(false)}>Annulla</Button>
            <Button onClick={() => submitExc(false)} disabled={saving} data-testid="exc-save">Aggiungi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog conflitti */}
      <Dialog open={!!conflict} onOpenChange={(o) => !o && setConflict(null)}>
        <DialogContent className="max-w-lg" data-testid="salon-conflict-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-[hsl(var(--hi-gold))]" /> Appuntamenti in conflitto</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">Ci sono <b>{conflict?.count}</b> appuntamenti fuori dai nuovi orari. Nessun appuntamento verrà cancellato: puoi procedere e non verrà più offerta disponibilità in quelle fasce, oppure annullare.</p>
          <div className="max-h-64 overflow-y-auto hi-scroll space-y-1 mt-2">
            {(conflict?.conflicts || []).map((c) => (
              <div key={c.id} className="flex items-center justify-between text-sm rounded-md border border-border px-3 py-1.5">
                <span>{fmtDate(c.date)} · {c.time} · {c.client_name}</span>
                <span className="text-xs text-muted-foreground">{c.operator_name}</span>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConflict(null)}>Annulla</Button>
            <Button onClick={() => conflict?.onConfirm?.()} disabled={saving} data-testid="salon-conflict-confirm">Procedi comunque</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
